import { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { NikoProjectExtended } from '../../types/project';

gsap.registerPlugin(ScrollTrigger);

interface ProjectSolutionsProps {
  project: NikoProjectExtended;
}

export function ProjectSolutions({ project }: ProjectSolutionsProps) {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let ctx: gsap.Context;

    const timer = setTimeout(() => {
      ctx = gsap.context(() => {
        gsap.fromTo(
          '.solution-card',
          { y: 60, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.8,
            stagger: 0.15,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: sectionRef.current,
              start: 'top 80%',
            },
          }
        );
        ScrollTrigger.refresh();
      }, sectionRef);
    }, 150);

    return () => {
      clearTimeout(timer);
      if (ctx) ctx.revert();
    };
  }, []);

  if (!project.solutions || project.solutions.length === 0) {
    return null;
  }

  return (
    <section ref={sectionRef} className="py-24 px-8 md:px-12 lg:px-24">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-xs text-white/30 tracking-[0.3em] uppercase mb-12">
          Lösungsansätze
        </h2>

        <div className="space-y-8">
          {project.solutions.map((solution, index) => (
            <div
              key={index}
              className="solution-card group grid md:grid-cols-12 gap-8 p-8 border border-white/10 rounded-xl hover:border-emerald-500/30 transition-colors"
            >
              {/* Number & Title */}
              <div className="md:col-span-4">
                <div className="flex items-start gap-4">
                  <span className="text-sm text-emerald-500/60 font-mono">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <h3 className="text-xl text-white font-medium group-hover:text-emerald-500 transition-colors">
                    {solution.title}
                  </h3>
                </div>
              </div>

              {/* Description */}
              <div className="md:col-span-5">
                <p className="text-white/60 leading-relaxed">
                  {solution.description}
                </p>
              </div>

              {/* Tech */}
              <div className="md:col-span-3">
                {solution.tech && solution.tech.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {solution.tech.map((tech) => (
                      <span
                        key={tech}
                        className="px-3 py-1 text-xs text-white/40 bg-white/5 rounded-full"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
